import React, { useEffect, useRef } from 'react';
import { getIn } from 'formik';
import { Button, Form, InputGroup } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
  MAX_QUOTE_PRODUCTS, MAX_QUOTE_MONEY, MAX_QUOTE_QUANTITY,
  newQuoteProduct, quoteProductSummary, suggestedProductTotal,
} from '../utils/quoteProducts';
import SearchableSelect, { buildSelectOptions } from './SearchableSelect';
import './QuoteProductsTable.css';

const columns = [
  { field: 'product_code', label: 'Product Code' },
  { field: 'principal_id', label: 'Principal', selection: true },
  { field: 'category_id', label: 'Product Category', selection: true },
  { field: 'qty', label: 'Quantity' },
  { field: 'unit_price', label: 'Unit Price', money: true },
  { field: 'total', label: 'Total', money: true },
];

export default function QuoteProductsTable({ formik, principalOptions = [], categories = [], classificationDisabled = false, showClassification = true, classificationInfo = null, compactRows = false }) {
  const visibleColumns = columns.filter(column => showClassification || !column.selection)
    .map(column => classificationInfo && column.selection
      ? { ...column, label: column.field === 'principal_id' ? 'Principal / Vendor' : 'Category' } : column);
  const rows = formik.values.products || [];
  const tableContainer = useRef(null);
  useEffect(() => {
    const resizeCodes = () => {
      tableContainer.current?.querySelectorAll('textarea').forEach(element => {
        element.style.height = 'auto';
        element.style.height = Math.max(76, element.scrollHeight + 2) + 'px';
      });
    };
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(resizeCodes);
    if (tableContainer.current) observer.observe(tableContainer.current);
    return () => observer.disconnect();
  }, []);
  const summary = quoteProductSummary(rows);
  const disabled = formik.isSubmitting;
  const change = (index, field, value) => {
    if (classificationDisabled && ['principal_id', 'category_id'].includes(field)) return;
    formik.setValues(values => ({
      ...values,
      products: values.products.map((row, rowIndex) => {
        if (rowIndex !== index) return row;
        const next = { ...row, [field]: value };
        if (field === 'principal_id') next.category_id = '';
        if (field === 'qty' || field === 'unit_price') {
          const calculated = suggestedProductTotal(next.qty, next.unit_price);
          if (calculated !== null) next.total = calculated;
        }
        return next;
      }),
    }));
  };
  const remove = index => {
    // Keep validation and touched state attached to the remaining rows.
    const touched = formik.touched.products;
    const errors = formik.errors.products;
    if (Array.isArray(touched)) formik.setTouched({ ...formik.touched, products: touched.filter((_, i) => i !== index) }, false);
    if (Array.isArray(errors)) formik.setErrors({ ...formik.errors, products: errors.filter((_, i) => i !== index) });
    formik.setFieldValue('products', rows.filter((_, i) => i !== index));
  };
  const createRow = (source = formik.values) => ({
    ...newQuoteProduct(),
    ...(classificationDisabled ? {
      principal_id: source.principal_id || '',
      category_id: source.category_id || '',
    } : {}),
  });
  const addAfter = index => {
    if (rows.length >= MAX_QUOTE_PRODUCTS) return;
    const next = [...rows];
    next.splice(index + 1, 0, createRow(rows[index]));
    formik.setFieldValue('products', next);
  };
  const addFirst = () => {
    if (rows.length >= MAX_QUOTE_PRODUCTS) return;
    formik.setFieldValue('products', [createRow()]);
  };
  const globalError = typeof formik.errors.products === 'string' && (formik.touched.products || formik.submitCount > 0)
    ? formik.errors.products : '';

  return (
    <section className={"quote-products" + (showClassification ? " quote-products--classification" : "") + (compactRows ? " quote-products--compact" : "")} aria-label="Product Quote" aria-describedby="quote-products-help">
      <div className="quote-products__heading">
        <h5>Product Quote</h5>
        <span>{rows.length} {rows.length === 1 ? 'product' : 'products'}</span>
      </div>
      <p className="quote-products__hint" id="quote-products-help">
        Total is calculated from quantity × unit price. You can edit the total if needed.
      </p>
      <div className="quote-products__scroll quote-products__editor" ref={tableContainer}>
        <table className="quote-products__table" aria-label="Quote products">
          <colgroup>{(showClassification ? ['20%', '16%', '16%', '10%', '16%', '16%', '6%'] : ['30%', '16%', '22%', '22%', '10%']).map((width, index) => <col key={index} style={{ width }} />)}</colgroup>
          <thead><tr>{visibleColumns.map(column => <th scope="col" key={column.field}>{column.field === 'qty' ? 'QTY' : column.label} {!(classificationInfo && column.selection) && <span className="text-danger">*</span>}</th>)}<th scope="col"><span className="visually-hidden">Actions</span></th></tr></thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row._key || row.id || index} style={compactRows ? {
                '--po-unit-price-width': Math.max(160, Math.min(16, String(row.unit_price ?? '').length) * 9 + 70) + 'px',
                '--po-total-width': Math.max(160, Math.min(16, String(row.total ?? '').length) * 9 + 70) + 'px',
              } : undefined}>
                {visibleColumns.map(column => {
                  const name = 'products[' + index + '].' + column.field;
                  const error = getIn(formik.errors, name);
                  const showError = !!error && (getIn(formik.touched, name) || formik.submitCount > 0);
                  const inputId = 'quote-product-' + index + '-' + column.field;
                  if (column.selection && classificationInfo) {
                    return <td key={column.field} data-field={column.field}>
                      <div className="quote-products__field-label">{column.label}</div>
                      <div className="quote-products__info">{classificationInfo[column.field]}</div>
                    </td>;
                  }
                  if (column.selection) {
                    const options = column.field === 'principal_id' ? principalOptions : buildSelectOptions(categories, {
                      filter: category => String(category.principal_id) === String(row.principal_id),
                    });
                    return <td key={column.field} data-field={column.field} data-label={column.field === 'qty' ? 'QTY' : column.label}>
                      <div className="quote-products__field-label" aria-hidden="true">{column.label} <span className="text-danger">*</span></div>
                      <label htmlFor={inputId} className="visually-hidden">{column.label + ' row ' + (index + 1)}</label>
                      <SearchableSelect inputId={inputId} name={name} options={options} value={row[column.field]}
                        onChange={value => change(index, column.field, value)}
                        onBlur={() => formik.setFieldTouched(name, true)}
                        isDisabled={disabled || classificationDisabled || (column.field === 'category_id' && !row.principal_id)}
                        placeholder={column.field === 'principal_id' ? 'Select principal...' : 'Select category...'}
                        isInvalid={!!showError} errorMessage={showError ? error : undefined} />
                    </td>;
                  }
                  const control = <Form.Control
                    id={inputId} name={name} aria-label={column.label + ' row ' + (index + 1)}
                    as={column.field === 'product_code' ? 'textarea' : undefined}
                    rows={column.field === 'product_code' ? 1 : undefined}
                    ref={column.field === 'product_code' ? element => {
                      if (element) {
                        element.style.height = 'auto';
                        element.style.height = Math.max(76, element.scrollHeight + 2) + 'px';
                      }
                    } : undefined}
                    type={column.field === 'product_code' ? undefined : 'number'}
                    inputMode={column.field === 'qty' ? 'numeric' : column.money ? 'decimal' : undefined}
                    min={column.field === 'qty' ? 1 : column.money ? 0 : undefined}
                    max={column.field === 'qty' ? MAX_QUOTE_QUANTITY : column.money ? MAX_QUOTE_MONEY : undefined}
                    step={column.money ? '0.01' : column.field === 'qty' ? '1' : undefined}
                    maxLength={column.field === 'product_code' ? 1000 : undefined}
                    value={row[column.field]} onChange={event => change(index, column.field, event.target.value)}
                    onBlur={formik.handleBlur} disabled={disabled} isInvalid={!!showError}
                    aria-invalid={!!showError} aria-describedby={showError ? inputId + '-error' : undefined}
                    placeholder={column.field === 'product_code' ? 'Enter product code' : column.money ? '0.00' : 'Qty'}
                  />;
                  return <td key={column.field} data-field={column.field} data-label={column.field === 'qty' ? 'QTY' : column.label}>
                    <div className="quote-products__field-label" aria-hidden="true">{column.field === 'qty' ? 'QTY' : column.label} <span className="text-danger">*</span></div>
                    {column.money ? <InputGroup className="quote-products__money"><InputGroup.Text>₹</InputGroup.Text>{control}</InputGroup> : control}
                    {showError && <div id={inputId + '-error'} className="invalid-feedback d-block" role="alert">{error}</div>}
                  </td>;
                })}
                <td className="quote-products__actions">
                  <Button type="button" variant="outline-primary" size="sm"
                    aria-label={'Add product row ' + (index + 1)} title="Add product row"
                    disabled={disabled || rows.length >= MAX_QUOTE_PRODUCTS} onClick={() => addAfter(index)}>
                    <FontAwesomeIcon icon={faPlus} />
                  </Button>
                  <Button type="button" variant="outline-danger" size="sm"
                    aria-label={'Delete product row ' + (index + 1)} title="Delete product row" disabled={disabled} onClick={() => remove(index)}>
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </Button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={visibleColumns.length + 1} className="quote-products__empty">
              No product rows.
              <Button type="button" variant="outline-primary" size="sm" className="ms-2"
                aria-label="Add product row 1" title="Add product row" disabled={disabled} onClick={addFirst}>
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            </td></tr>}
          </tbody>
        </table>
      </div>
      {globalError && <div className="invalid-feedback d-block" role="alert">{globalError}</div>}
      <div className="quote-products__footer">
        <div className="quote-products__totals">
          <span>Total quantity <strong>{summary.qty}</strong></span>
          <span>Quote total <strong aria-live="polite">₹ {summary.total}</strong></span>
        </div>
      </div>
    </section>
  );
}
